import { useCallback, useState } from 'react';
import { api, type ValidateNodeRequest, type ValidationResult } from '../lib/api-client';
import type { CustomNodeData } from '../types/api';

/**
 * Transform frontend form configuration to backend-expected structure
 * This converts the frontend's flat structure to the backend's metadata + formSchema structure
 */
function transformFormConfigForValidation(
	frontendConfig: Record<string, any>
): Record<string, any> {
	// If it's already in the correct format, return as-is
	if (frontendConfig.metadata && frontendConfig.formSchema) {
		return frontendConfig;
	}

	// Transform frontend structure to backend structure
	const transformedConfig: Record<string, any> = {};

	// Handle metadata transformation
	if (frontendConfig.name || frontendConfig.description) {
		transformedConfig.metadata = {
			title: frontendConfig.name || 'Untitled Form',
			description: frontendConfig.description || 'No description provided',
		};
	}

	// Handle formSchema transformation
	if (frontendConfig.fields && Array.isArray(frontendConfig.fields)) {
		const properties: Record<string, any> = {};
		const required: string[] = [];

		frontendConfig.fields.forEach((field: any) => {
			const fieldId = field.fieldId || field.name;
			if (fieldId) {
				// Convert field to JSON Schema property
				properties[fieldId] = {
					type: field.type === 'email' || field.type === 'text' ? 'string' : field.type,
					title: field.label || field.name,
					description: field.placeholder || undefined,
					...field.validation, // Include minLength, maxLength, min, max, pattern, etc.
				};

				// Add to required array if field is required
				if (field.required) {
					required.push(fieldId);
				}
			}
		});

		transformedConfig.formSchema = {
			type: 'object',
			properties,
			required,
		};
	}

	// Copy any other properties that might be needed
	Object.keys(frontendConfig).forEach(key => {
		if (!['name', 'description', 'fields'].includes(key)) {
			transformedConfig[key] = frontendConfig[key];
		}
	});

	// Debug logging disabled to prevent console spam
	// console.log('🔄 Transformed form config:', {
	//	original: frontendConfig,
	//	transformed: transformedConfig,
	// });

	return transformedConfig;
}

export interface UseNodeValidationReturn {
	isValidating: boolean;
	lastValidationResult: ValidationResult | null;
	validateNode: (nodeId: string, nodeData: CustomNodeData) => Promise<ValidationResult>;
	clearValidationResult: () => void;
}

/**
 * Hook for validating individual nodes using the backend validation API
 */
export function useNodeValidation(): UseNodeValidationReturn {
	const [isValidating, setIsValidating] = useState(false);
	const [lastValidationResult, setLastValidationResult] = useState<ValidationResult | null>(null);

	const validateNode = useCallback(
		async (nodeId: string, nodeData: CustomNodeData): Promise<ValidationResult> => {
			// console.log('🔍 Starting node validation for:', nodeId, nodeData);
			setIsValidating(true);

			try {
				// Transform the configuration if it's a form trigger node
				let transformedNodeData = nodeData.config || {};
				if (nodeData.type === 'start.form') {
					transformedNodeData = transformFormConfigForValidation(transformedNodeData);
				}

				// Prepare the validation request
				const request: ValidateNodeRequest = {
					nodeType: nodeData.type,
					nodeData: transformedNodeData,
					mode: 'strict', // Use strict validation for faster response (was 'both')
					nodeId,
				};

				// console.log('📤 Sending validation request:', request);
				// console.log('🔄 Transformation details:', {
				//	originalConfig: nodeData.config,
				//	transformedConfig: transformedNodeData,
				//	hasMetadata: !!transformedNodeData.metadata,
				//	hasFormSchema: !!transformedNodeData.formSchema,
				// });
				// Debug logging disabled to prevent console spam
				// console.log('🌐 API URL being used:', import.meta.env.VITE_API_URL || 'Using Vite proxy');

				// Call the validation API
				const result = await api.validation.validateNode(request);

				// Debug logging disabled to prevent console spam
				// console.log('✅ Validation result received:', result);
				// console.log('📊 Response details:', { valid: result.valid, errorCount: result.errors?.length || 0 });

				// Store the result
				setLastValidationResult(result);

				return result;
			} catch (error: any) {
				// Debug logging disabled to prevent console spam
				// console.error('❌ Validation failed:', {
				//	error: error.message,
				//	response: error.response?.data,
				//	status: error.response?.status,
				//	statusText: error.response?.statusText,
				// });

				// Create a fallback validation result for errors
				const fallbackResult: ValidationResult = {
					valid: false,
					errors: [
						{
							field: 'validation',
							message: `Validation failed: ${error.message}`,
							severity: 'error',
							code: 'VALIDATION_ERROR',
						},
					],
					metadata: {
						validationTime: 0,
						mode: 'strict',
						aiEnhanced: false,
					},
				};

				setLastValidationResult(fallbackResult);
				throw error;
			} finally {
				setIsValidating(false);
			}
		},
		[]
	);

	const clearValidationResult = useCallback(() => {
		setLastValidationResult(null);
	}, []);

	return {
		isValidating,
		lastValidationResult,
		validateNode,
		clearValidationResult,
	};
}

/**
 * Hook for implementing the consistent validation pattern across all node configurations
 * This provides the two-step validation logic: first click validates and shows errors,
 * second click allows saving even if validation failed
 */
export function useNodeValidationPattern(nodeId: string, nodeData: CustomNodeData) {
	const { validateNode } = useNodeValidation();
	const [validationAttempted, setValidationAttempted] = useState(false);
	const [validationFailed, setValidationFailed] = useState(false);

	/**
	 * Perform validation and return whether it passed
	 * This can be used by the onValidationRequest callback
	 */
	const performValidation = useCallback(async (): Promise<boolean> => {
		try {
			const result = await validateNode(nodeId, nodeData);

			// Return true if validation passed, false if there are critical errors
			return result.valid || !result.errors.some(error => error.severity === 'error');
		} catch (error) {
			console.error('Validation failed:', error);
			return false;
		}
	}, [validateNode, nodeId, nodeData]);

	/**
	 * Handle the complete wizard action with two-step validation
	 * This implements the pattern: first click validates and stays in node if failed,
	 * second click allows exit regardless of validation result
	 */
	const handleCompleteWizard = useCallback(
		async (onSave?: () => void): Promise<void> => {
			// Step 1: If validation hasn't been attempted yet, run validation and stay in node
			if (!validationAttempted) {
				try {
					const result = await validateNode(nodeId, nodeData);
					setValidationAttempted(true);

					if (!result.valid) {
						// Validation failed - stay in node and show errors
						setValidationFailed(true);
						console.log('Validation failed, staying in node:', result);
						return; // Don't exit the node
					} else {
						// Validation succeeded - proceed to exit
						setValidationFailed(false);
						console.log('Validation succeeded:', result);
					}
				} catch (error) {
					console.error('Validation failed:', error);
					setValidationAttempted(true);
					setValidationFailed(true);
					return; // Don't exit the node on error
				}
			}

			// Step 2: If validation was already attempted (regardless of success/failure), exit the node
			// This allows users to force exit even if validation failed

			// Call the save function if provided
			if (onSave) {
				onSave();
			}

			// Reset validation state for next time
			setValidationAttempted(false);
			setValidationFailed(false);
		},
		[validateNode, nodeId, nodeData, validationAttempted]
	);

	return {
		validationAttempted,
		validationFailed,
		performValidation,
		handleCompleteWizard,
		setValidationAttempted,
		setValidationFailed,
	};
}
